import type {
  CadenceAnchor,
  CycleDesignerFormState,
  DesignerPath,
  OrgPathNode,
  PathSlotId,
} from '../types';
import { buildOrgById, pathDepthOf, pathParentOf } from './pathBuilder';

/**
 * Cadence schedule — the calendar a cycle design implies ("the method arrives
 * with its calendar", the V2MOM lesson).
 *
 * Pure derivation, two anchor modes:
 *  - 'start' (forward): level due dates accumulate downward from launch day.
 *  - 'lock' (T-minus):  the last level lands on the end date (T-0) and every
 *    level above is counted back from it — including the date the ball must
 *    leave the top ("publish by").
 *
 * The same derivation feeds the review-step preview, the launch persistence
 * (track metadata + level config + the first request's due date), and the
 * tests. launchCycleDesigner.ts consumes it at launch time.
 */

const MS_PER_DAY = 86_400_000;

/** One stop in a path's descent, with its computed deadline. */
export interface CadenceStop {
  nodeId: string;
  /** Org unit display name (falls back to the id when the roster lacks it). */
  nodeName: string;
  /** 0-based position in the descent; 0 = the cycle's org anchor. */
  level: number;
  /** Response window granted to this stop, in days. 0 for the anchor. */
  windowDays: number;
  /** Cumulative days from the top to this stop's due date. */
  offsetDays: number;
  /** ISO date (YYYY-MM-DD) the stop's response is due; null when the anchoring date is absent. */
  dueDate: string | null;
  /** Human label when no date is computable ("Launch + Nd" / "Lock − Nd"). */
  relativeLabel: string;
  /** "T−Nd" relative to endDate ("T+Nd" past it); null when not computable. */
  tMinusLabel: string | null;
}

export interface PathCadence {
  pathId: PathSlotId;
  pathName: string;
  color: string;
  stops: CadenceStop[];
  /** Sum of the response windows below the anchor. */
  totalDescentDays: number;
  /** True when the descent needs more days than the cycle window offers. */
  overrun: boolean;
  /**
   * Lock mode only: the date the ball must leave the top for the last level
   * to land on T-0 (endDate − totalDescentDays). Null in start mode or when
   * endDate is absent.
   */
  publishBy: string | null;
}

export interface CadenceSchedule {
  anchor: CadenceAnchor;
  /** ISO start date, or null when the form has none ("on launch" mode). */
  startDate: string | null;
  /** ISO end date, or null when the form has none. */
  endDate: string | null;
  /** Days from startDate to endDate; null unless both dates are set. */
  daysAvailable: number | null;
  paths: PathCadence[];
}

/**
 * Response window for a level of a path's descent: the per-node override when
 * one is set, else the path default (params.deadlineDays). This is the single
 * resolution point shared by the preview, the launch persistence, and the
 * level config.
 */
export function resolveResponseWindowDays(path: DesignerPath, nodeId: string): number {
  const override = path.nodeWindows?.[nodeId];
  if (typeof override === 'number' && Number.isFinite(override) && override > 0) {
    return Math.round(override);
  }
  return path.params.deadlineDays;
}

function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}

function tMinusLabel(due: Date, end: Date): string {
  const remaining = daysBetween(due, end);
  return remaining >= 0 ? `T−${remaining}d` : `T+${Math.abs(remaining)}d`;
}

/**
 * Derive the level-by-level calendar each path creates.
 *
 * Start mode: level 0 is the anchor (launch day: form.startDate, or "on
 * launch" when absent); each subsequent stop's due date is the previous
 * stop's due date plus that level's response window.
 *
 * Lock mode: the last stop's due date is form.endDate (T-0); each stop above
 * is that stop's due minus the stop below's window. The anchor stop's date is
 * when the ball must leave the top ("publish by").
 *
 * A path that forks is a tree: a stop's offset is the offset of the stop above
 * it plus its own window, so two branches below one stop run side by side and
 * the path takes as long as its longest branch.
 */
export function buildCadenceSchedule(
  form: Pick<CycleDesignerFormState, 'startDate' | 'endDate' | 'cadenceAnchor'>,
  paths: DesignerPath[],
  orgNodes: OrgPathNode[],
): CadenceSchedule {
  const anchor: CadenceAnchor = form.cadenceAnchor ?? 'start';
  const start = parseIsoDate(form.startDate);
  const end = parseIsoDate(form.endDate);
  const daysAvailable = start && end ? daysBetween(start, end) : null;
  const nameById = new Map(orgNodes.map((n) => [n.id, n.name]));
  const orgById = buildOrgById(orgNodes);

  const pathCadences: PathCadence[] = paths.map((path) => {
    // Windows and cumulative offsets from the top — shared by both modes.
    const windows = path.nodes.map((nodeId, index) =>
      index === 0 ? 0 : resolveResponseWindowDays(path, nodeId),
    );
    const offsetById = new Map<string, number>();
    path.nodes.forEach((nodeId, index) => {
      const parentId = pathParentOf(path, nodeId, orgById);
      offsetById.set(nodeId, (parentId ? offsetById.get(parentId) ?? 0 : 0) + windows[index]);
    });
    const totalDescentDays = Math.max(0, ...offsetById.values());

    // The date each mode anchors on. In lock mode every stop's due date is
    // endDate − (totalDescent − offset); in start mode it is start + offset.
    const anchorDate = anchor === 'lock' ? end : start;

    const stops: CadenceStop[] = path.nodes.map((nodeId, index) => {
      const offsetDays = offsetById.get(nodeId) ?? 0;
      const level = pathDepthOf(path, nodeId, orgById);
      const remainingDays = totalDescentDays - offsetDays;
      const due = anchorDate
        ? anchor === 'lock'
          ? addDays(anchorDate, -remainingDays)
          : addDays(anchorDate, offsetDays)
        : null;
      const relativeLabel =
        anchor === 'lock'
          ? remainingDays === 0
            ? 'Lock day (T−0)'
            : level === 0
              ? `Publish by Lock − ${remainingDays}d`
              : `Lock − ${remainingDays}d`
          : level === 0
            ? 'Launch day'
            : `Launch + ${offsetDays}d`;
      return {
        nodeId,
        nodeName: nameById.get(nodeId) ?? nodeId,
        level,
        windowDays: windows[index],
        offsetDays,
        dueDate: due ? toIsoDate(due) : null,
        relativeLabel,
        tMinusLabel: due && end ? tMinusLabel(due, end) : null,
      };
    });

    return {
      pathId: path.id,
      pathName: path.name,
      color: path.color,
      stops,
      totalDescentDays,
      overrun: daysAvailable !== null && totalDescentDays > daysAvailable,
      publishBy:
        anchor === 'lock' && end ? toIsoDate(addDays(end, -totalDescentDays)) : null,
    };
  });

  return {
    anchor,
    startDate: start ? toIsoDate(start) : null,
    endDate: end ? toIsoDate(end) : null,
    daysAvailable,
    paths: pathCadences,
  };
}

/** The cadence for one path, or null when the path isn't in the schedule. */
export function pathCadenceFor(
  schedule: CadenceSchedule,
  pathId: PathSlotId,
): PathCadence | null {
  return schedule.paths.find((p) => p.pathId === pathId) ?? null;
}

/**
 * The due date the FIRST toss should carry: the level-1 stop's computed due
 * date. Null when the design has no second stop or no computable dates —
 * callers fall back to the cycle end date.
 */
export function firstTossDueDate(cadence: PathCadence | null): string | null {
  if (!cadence) return null;
  // A path that forks at the top has several level-1 stops; the first toss
  // carries the earliest of their dates.
  const dates = cadence.stops
    .filter((s) => s.level === 1 && s.dueDate)
    .map((s) => s.dueDate as string)
    .sort();
  return dates[0] ?? null;
}
